import React, {FC, useEffect, useState} from 'react'
import axios from 'axios';
import DialogContent from '@pixel/core/DialogContent'
import DialogActions from '@pixel/core/DialogActions'
import { useForm, FormProvider } from 'react-hook-form'
import AutomationForm from './AutomationForm'
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes'
import LocalizedButton from '../../components/LocalizedButton'
import {AutomationSearchResult, DefaultAutomationType} from '../types/Automations'
import {useMutation} from "@tanstack/react-query";
import {Alert, Snackbar} from "@mui/material";
import {useMultipleLocalizedText} from "../../contexts/Localization/localizationHooks";

interface AutomationFormContainerProps {
    selectedAutomation: AutomationSearchResult
    onModalClose: () => void
    fetchData: () => void
}

const AutomationFormContainer: FC<AutomationFormContainerProps> = ({
    selectedAutomation,
    onModalClose,
    fetchData
}) => {

    const {
        AUTOMATION_FORM_ALERT_MESSAGE_SUCCESS,
        AUTOMATION_FORM_ALERT_MESSAGE_ERROR,
        AUTOMATION_EDIT_ALERT_MESSAGE_SUCCESS,
        AUTOMATION_EDIT_ALERT_MESSAGE_ERROR
    } = useMultipleLocalizedText()

    const [formSubmitted, setFormSubmitted] = useState(false)
    const [formResponse, setFormResponse] = useState(null)
    const [formMessage, setFormMessage] = useState('')
    const [errors, setErrors] = useState([])

    const form = useForm({
        defaultValues: {
            name: '',
            automationType: '',
            automationObject: 'REQUISITION',
            companyFilters: [
                {
                }
            ],
            companyActions: [
                {
                }
            ],
            linkedAutomations: [],
            actionUserEId: '',
        },
    })

    const CREATE_NEW_AUTOMATION_ENDPOINT = '/admin/action/automation/admin/rule';

    const submitData = async (data: any) => {
        let doFetchData = true;
        if (!data.linkedAutomations[0]?.secondaryCompanyAutomation?.companyAutomationId) {
            data.linkedAutomations = [];
        }

        if (data.automationType === DefaultAutomationType) {
            if (!data.actionUser) {
                data.actionUserEId = "";
            }
            if (data.actionUserEId === "" || data.actionUserEId === "none") {
                form.setValue("actionUserEId", "none");
                return;
            }
        }

        if(data.triggerType === "CANDIDATE_WORKFLOW_STATE") {
            data.automationObject = "CANDIDATE";
        } else {
            data.automationObject = "REQUISITION";
        }

        try {
            const response = await axios.post(CREATE_NEW_AUTOMATION_ENDPOINT, data);
            if (response.status === 201 || response.status === 200) {
                    if (response.data.validationError) {
                        setErrors(response.data.errors)
                        setFormResponse('error');
                    if (response.data.message) {
                        setFormMessage(response.data.message);
                    } else {
                        setFormMessage(AUTOMATION_EDIT_ALERT_MESSAGE_ERROR);

                    }
                    doFetchData = false;
                }

                else {
                    setFormResponse('success');
                    if (selectedAutomation?.companyAutomationId) {
                        setFormMessage(AUTOMATION_EDIT_ALERT_MESSAGE_SUCCESS);
                    } else {
                        setFormMessage(AUTOMATION_FORM_ALERT_MESSAGE_SUCCESS);
                    }
                }

            } else {
                doFetchData = false;
                setFormResponse('error');
                if (selectedAutomation?.companyAutomationId) {
                    setFormMessage(AUTOMATION_EDIT_ALERT_MESSAGE_ERROR);
                }
                setFormMessage(AUTOMATION_FORM_ALERT_MESSAGE_ERROR);
            }
        } catch (e) {
            setFormResponse('error');
            setFormMessage(AUTOMATION_FORM_ALERT_MESSAGE_ERROR);
        }

        setFormSubmitted(true);
        if (doFetchData) {
            fetchData();
        }
    }

    const mutation = useMutation(submitData);

    const onSubmit = (data: any) => {
        mutation.mutate(data);
    }

    useEffect(() => {
        if (Boolean(selectedAutomation)) {
            form.reset(selectedAutomation)
        }
    }, [selectedAutomation])

    return (
        <FormProvider {...form}>
            <Snackbar open={formSubmitted} autoHideDuration={1000} onClose={() => {
                setFormSubmitted(false)
                if (formResponse == 'success') {
                    onModalClose()
                }
            }}>
                <Alert onClose={() => setFormSubmitted(false)} severity={formResponse} sx={{ width: '100%' }}>
                    {formMessage}
                </Alert>
            </Snackbar>
            <DialogContent sx={{ padding: 0 }}>
                <AutomationForm selectedAutomation={selectedAutomation} errors={errors} setErrors={setErrors}/>
            </DialogContent>
            <DialogActions>
                <LocalizedButton
                    id='automation-form-cancel-button'
                    text={LocalizedText.Cancel}
                    variant="outlined"
                    color="secondary"
                    onClick={onModalClose}
                />
                <LocalizedButton
                    id='automation-form-save-button'
                    text={LocalizedText.AUTOMATION_FORM_SAVE}
                    variant="contained"
                    color="primary"
                    onClick={form.handleSubmit(onSubmit)}
                />
            </DialogActions>
        </FormProvider>
    )
}

export default AutomationFormContainer
